import {useOptionalCommitMessageGenerationConfig} from '@github-ui/pull-requests/layout-selectors'
import type {ReactNode} from 'react'
import {
  SuggestionStates,
  type AutomatedCommentSourceType,
  type CommitMessageGenerationConfig,
  type Suggestion,
  type SuggestionState,
} from '../../../conversation-types'
import {AssignToCopilotButton, type AssignToCopilotProps} from './AssignToCopilotButton'
import {
  AutomatedReviewSuggestionApplied,
  AutomatedReviewSuggestionError,
  AutomatedReviewSuggestionOutdated,
  AutomatedReviewSuggestionPending,
  AutomatedReviewSuggestionPresent,
} from './AutomatedReviewSuggestionStates'

export interface AutomatedReviewSuggestionProps {
  automatedCommentId: string
  basePath: string
  foldLongSuggestion?: boolean
  commentAuthorLogin?: string
  commentDatabaseId?: number | null
  commentTitle?: string
  /** The automated comment's source (e.g. `copilot`, `code_scanning`); used to gate CCR-only behavior. */
  commentSource?: AutomatedCommentSourceType
  /** Backend-provided auth config for generating a commit title; only honored for Copilot (CCR) suggestions. */
  commitMessageGenerationConfig?: CommitMessageGenerationConfig | null
  feedbackOptions?: Array<{label: string; value: string}>
  isAgenticAutofixEnabled?: boolean
  isClassicExperience?: boolean
  isDismissed?: boolean
  suggestion?: Suggestion
  suggestionState?: SuggestionState
  suggestionErrorMessage?: string | null
  threadId?: string
  fixWithCopilotProps?: {
    commentPermalink: string
    modelsUrl?: string
  }
  askCopilotAction?: ReactNode
  assignToCopilotProps?: AssignToCopilotProps
}

const SuggestionStateComponents = {
  [SuggestionStates.Pending]: AutomatedReviewSuggestionPending,
  [SuggestionStates.Error]: AutomatedReviewSuggestionError,
  [SuggestionStates.Outdated]: AutomatedReviewSuggestionOutdated,
  [SuggestionStates.Applied]: AutomatedReviewSuggestionApplied,
  [SuggestionStates.Present]: AutomatedReviewSuggestionPresent, // Default suggestion component
} as const

type RenderableSuggestionState = keyof typeof SuggestionStateComponents

export function AutomatedReviewSuggestion({
  automatedCommentId,
  basePath,
  foldLongSuggestion,
  commentAuthorLogin,
  commentDatabaseId,
  commentTitle,
  commentSource,
  commitMessageGenerationConfig,
  feedbackOptions,
  fixWithCopilotProps,
  askCopilotAction,
  assignToCopilotProps,
  isAgenticAutofixEnabled = false,
  isClassicExperience,
  isDismissed,
  suggestion,
  suggestionState,
  suggestionErrorMessage,
  threadId,
}: AutomatedReviewSuggestionProps) {
  if (isAgenticAutofixEnabled) {
    return assignToCopilotProps ? <AssignToCopilotButton {...assignToCopilotProps} basePageDataUrl={basePath} /> : null
  }

  if (!suggestionState || suggestionState === SuggestionStates.NoSuggestion) {
    return null
  }

  return (
    <AutomatedReviewSuggestionContent
      automatedCommentId={automatedCommentId}
      basePath={basePath}
      foldLongSuggestion={foldLongSuggestion}
      commentAuthorLogin={commentAuthorLogin}
      commentDatabaseId={commentDatabaseId}
      commentTitle={commentTitle}
      commentSource={commentSource}
      commitMessageGenerationConfig={commitMessageGenerationConfig}
      feedbackOptions={feedbackOptions}
      fixWithCopilotProps={fixWithCopilotProps}
      askCopilotAction={askCopilotAction}
      isClassicExperience={isClassicExperience}
      isDismissed={isDismissed}
      suggestion={suggestion}
      suggestionState={suggestionState}
      suggestionErrorMessage={suggestionErrorMessage}
      threadId={threadId}
    />
  )
}

function AutomatedReviewSuggestionContent({
  automatedCommentId,
  basePath,
  foldLongSuggestion,
  commentAuthorLogin,
  commentDatabaseId,
  commentTitle,
  commentSource,
  commitMessageGenerationConfig,
  feedbackOptions,
  fixWithCopilotProps,
  askCopilotAction,
  isClassicExperience,
  isDismissed,
  suggestion,
  suggestionState,
  suggestionErrorMessage,
  threadId,
}: AutomatedReviewSuggestionProps & {suggestionState: RenderableSuggestionState}) {
  const layoutCommitMessageGenerationConfig = useOptionalCommitMessageGenerationConfig()
  const SuggestionComponent = SuggestionStateComponents[suggestionState]
  const resolvedCommitMessageGenerationConfig =
    isClassicExperience || commitMessageGenerationConfig !== undefined
      ? commitMessageGenerationConfig
      : layoutCommitMessageGenerationConfig

  if (!SuggestionComponent) {
    return null
  }

  return (
    <SuggestionComponent
      automatedCommentId={automatedCommentId}
      basePath={basePath}
      foldLongSuggestion={foldLongSuggestion}
      commentTitle={commentTitle}
      commentAuthorLogin={commentAuthorLogin}
      commentDatabaseId={commentDatabaseId}
      commentSource={commentSource}
      commitMessageGenerationConfig={resolvedCommitMessageGenerationConfig}
      feedbackOptions={feedbackOptions}
      fixWithCopilotProps={fixWithCopilotProps}
      askCopilotAction={askCopilotAction}
      isClassicExperience={isClassicExperience}
      isDismissed={isDismissed}
      suggestion={suggestion}
      suggestionErrorMessage={suggestionErrorMessage}
      threadId={threadId}
    />
  )
}
